import type { FC } from 'react';
import React, { memo, useState, useEffect } from 'react';
import { useQuery } from 'react-apollo';
import type { ApolloError } from 'apollo-client';
import type { GraphQLError } from 'graphql';

import { useLivePagesStore } from '@confluence/live-pages-utils/entry-points/useLivePagesStore';
import { useIsLivePagesFeatureEnabled } from '@confluence/live-pages-utils/entry-points/useIsLivePagesFeatureEnabled';
import { ErrorDisplay, isStatusCodeError, isUnauthorizedError } from '@confluence/error-boundary';
import { useSessionData } from '@confluence/session-data';
import { markErrorAsHandled } from '@confluence/graphql';
import { containsNoNetworkError } from '@confluence/network';
import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage';

import { GeneralShortcutsEnabledQuery } from './GeneralShortcutsEnabledQuery.experimentalgraphql';
import { ShortcutListener, ShortcutType } from './ShortcutListener';
import { AppNavigationShortcut } from './AppNavigationShortcut';
import { useKeyboardShortcutsState } from './useKeyboardShortcutsState';

type GeneralShortcutListenerProps = {
	accelerator: string | string[];
	listener(e: KeyboardEvent): void;
	isAppNavigationShortcut?: boolean;
	shortcutType?: ShortcutType;
	allowInDialog?: boolean;
};

export const GeneralShortcutListener: FC<GeneralShortcutListenerProps> = memo(
	({
		accelerator,
		listener,
		isAppNavigationShortcut = false,
		shortcutType = ShortcutType.Confluence,
		allowInDialog = false,
	}) => {
		const [skipQueryIfUnauthorized, setSkipQueryIfUnauthorized] = useState(false);
		const { isLoggedIn, isLicensed } = useSessionData();
		const isOnEditRoute = useIsEditorPage();
		const [globalLiveState] = useLivePagesStore();
		const isLivePagesFeatureEnabled = useIsLivePagesFeatureEnabled();
		const [, keyboardShortActions] = useKeyboardShortcutsState();

		/** The Live Pages and Edit in Context features require disabling shortcuts bc they render the editor in
		 * the presence of general shortcut listeners that can conflict with typing.
		 */
		const disableForEditor = isOnEditRoute || (isLivePagesFeatureEnabled && globalLiveState.isLive);

		const skipOptions =
			!isLoggedIn ||
			!isLicensed ||
			skipQueryIfUnauthorized ||
			disableForEditor ||
			Boolean(process.env.REACT_SSR); // GeneralShortcutsEnabledQuery is not preloaded and can be skipped on SSR

		const { loading, error, data } = useQuery(GeneralShortcutsEnabledQuery, {
			skip: skipOptions,
			onError(apolloError: ApolloError) {
				const isUnauthorized = apolloError?.graphQLErrors?.some(
					(graphQLError: GraphQLError) =>
						isStatusCodeError(graphQLError, '401') || isUnauthorizedError(graphQLError),
				);
				setSkipQueryIfUnauthorized(isUnauthorized);

				// Unauthorized errors are marked as handled since anonymous access can cause error count for this query to spike
				// We can't do anything about network errors so it is marked as handled
				if (isUnauthorized || containsNoNetworkError(apolloError)) {
					markErrorAsHandled(apolloError);
				}
			},
		});

		useEffect(() => {
			const keyboardShortCutsEnabled = data?.experimentalKeyboardShortcutsStatus?.enabled;
			keyboardShortActions.setKeyboardShortcutsEnabled(keyboardShortCutsEnabled);
		}, [data?.experimentalKeyboardShortcutsStatus?.enabled, keyboardShortActions]);

		if (loading) {
			return null;
		} else if (error) {
			if (
				isStatusCodeError(error, '401') ||
				isUnauthorizedError(error) ||
				containsNoNetworkError(error)
			) {
				markErrorAsHandled(error);
				return null;
			}
			return <ErrorDisplay error={error} />;
		}

		const areGeneralShortcutsEnabled = data?.experimentalKeyboardShortcutsStatus?.enabled;

		if (!areGeneralShortcutsEnabled) return null;

		return isAppNavigationShortcut ? (
			<AppNavigationShortcut accelerator={accelerator} listener={listener} />
		) : (
			<ShortcutListener
				accelerator={accelerator}
				listener={listener}
				shortcutType={shortcutType}
				allowInDialog={allowInDialog}
			/>
		);
	},
);
